// modules
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { uniqBy } from "lodash";

// component
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// redux
import { getStoreList, getActiveGroupsList, createUser, usersDataLoadingSelector, getStoreListLoading } from "redux/userList/userSlice";

// hook
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// constants
import { Routes, NewUser, AccountManagement as Constants } from "constants/global";

// services & utils
import { OutletItem } from "services/api/types";
import { validateEmail, validateUsername } from "utils/Validations/fieldValidations";

type AdminData = {
	username: string;
	email: string;
};

type FieldErrors = {
	username?: boolean;
	email?: boolean;
};

export function useCreateAccountPage() {
	const loading = useAppSelector(usersDataLoadingSelector);
	const storeListLoading = useAppSelector(getStoreListLoading);

	const [searchSelected, setSearchSelected] = useState<boolean>(false);
	const [searchInput, setSearchInput] = useState<string | null>(null);
	const [selectedValue, setSelectedValue] = useState<string | null>(null);
	const [storesDataList, setStoresDataList] = useState<Array<OutletItem>>([]);
	const [adminData, setAdminData] = useState<AdminData>({
		username: "",
		email: ""
	});
	const [tenantCode, setTenantCode] = useState<string>();

	const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
		username: false,
		email: false
	});
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const handleAdminDataChange = useCallback(
		({ id, value }: { id: string; value: string }) => {
			const newData = {
				...adminData,
				[id]: value
			};
			setAdminData(newData);
		},
		[adminData, setAdminData]
	);

	const validateFields = useCallback(
		(submit: boolean) => {
			const createAccPageFieldsToValidate = [
				{ key: "username", validator: validateUsername, required: true },
				{ key: "email", validator: validateEmail, required: true }
			];

			setFieldErrors({
				username: false,
				email: false
			});

			const validationErrors: Array<{ field: string | undefined }> = [];

			createAccPageFieldsToValidate.forEach((field: { key: string; validator: (args: string) => boolean; required: boolean }) => {
				const key = field.key as keyof AdminData;
				const value = adminData[key];
				if (value || (submit && field?.required)) {
					const isValid = field.validator(value);

					if (!isValid) {
						validationErrors.push({ field: field.key });
					}
				}
			});

			if (validationErrors.length) {
				const errs: FieldErrors = {};
				validationErrors.forEach((err) => {
					errs[err.field as keyof FieldErrors] = true;
				});
				setFieldErrors(errs);
				return;
			}

			return true;
		},
		[adminData, setFieldErrors, validateUsername, validateEmail]
	);

	const onChangeHandler = (value: string) => {
		if (value) {
			setSelectedValue(value);
			const selectVal = value.split("*");
			setSearchInput(selectVal[0]);
			setTenantCode(selectVal[2]);
			setSearchSelected(true);
		} else {
			setSelectedValue(null);
			setSearchInput(null);
			setSearchSelected(false);
		}
	};

	const isCreateAdminButtonEnabled = useMemo(() => {
		const { username, email } = adminData;
		if (username && email && searchSelected) {
			return true;
		}
		return false;
	}, [adminData, searchSelected]);

	const goBack = useCallback(() => {
		navigate(`${Routes.ACCOUNT_MANAGEMENT}`);
	}, [navigate]);

	const isActiveGroupList = useCallback(async () => {
		try {
			const { data } = await dispatch(getActiveGroupsList({ status: "ACTIVE" })).unwrap();
			if (data?.length) {
				return true;
			} else {
				return false;
			}
		} catch (error) {
			const err = error as any;
			if (err?.response?.data?.errors && err?.response?.data?.errors?.length > 0) {
				CountDownModal({
					timer: 3,
					errorIcon: true,
					title: Constants.CREATE_ACCOUNT.CANNOT_SEND_REQUEST,
					onTimeComplete: goBack
				});
			}
		}
	}, [goBack, dispatch]);

	const getStoresInfo = useCallback(async () => {
		if (await isActiveGroupList()) {
			try {
				const { data } = await dispatch(getStoreList({})).unwrap();

				if (data) {
					setStoresDataList(data);
				}
			} catch (error) {
				const err = error as any;
				if (err?.response?.data?.errors && err?.response?.data?.errors?.length > 0) {
					CountDownModal({
						timer: 3,
						errorIcon: true,
						title: Constants.CREATE_ACCOUNT.CANNOT_SEND_REQUEST,
						onTimeComplete: goBack
					});
				}
			}
		} else {
			CountDownModal({
				timer: 5,
				title: Constants.CREATE_ACCOUNT.NO_ACTIVE_TENANT,
				errorIcon: true,
				onTimeComplete: goBack
			});
		}
	}, [dispatch, getStoreList, setStoresDataList]);

	const onSuccess = useCallback(() => {
		navigate(Routes.ACCOUNT_MANAGEMENT);
	}, [navigate]);

	const onFailure = useCallback(() => {
		navigate(`${Routes.ACCOUNT_MANAGEMENT}/${Routes.CREATE_ACCOUNT}`);
	}, [navigate]);

	const createUserSuccess = useCallback(
		(hasErrors: boolean, message: string | undefined, user_id = "") => {
			if (user_id && hasErrors && message === NewUser.NEW_USER_SUCCESS_MEESSAGE) {
				CountDownModal({
					timer: 3,
					closable: true,
					title: Constants.CREATE_ACCOUNT.TENANT_SUCCESS_CREATED,
					subtitle: Constants.CREATE_ACCOUNT.TENANT_SUCCESS_SUBTITLE,
					okButton: {
						label: Constants.CREATE_ACCOUNT.GOT_IT,
						onClick: onSuccess
					},
					onTimeComplete: goBack
				});
			}
		},
		[CountDownModal, onSuccess]
	);

	const createUserFailure = useCallback(
		(errorObj: Array<{ message: string }>) => {
			if (
				errorObj?.length &&
				(errorObj[0]?.message?.includes(NewUser?.USER_ALREADY_EXISTS) ||
					errorObj[0]?.message?.includes(NewUser?.EMAIL_ALREADY_EXISTS) ||
					errorObj[0]?.message?.includes(NewUser?.USERNAME_ALREADY_EXISTS))
			) {
				CountDownModal({
					timer: 3,
					errorIcon: true,
					title: NewUser.NEW_USER_ALREADY_EXISTS,
					onTimeComplete: onFailure
				});
			}
		},
		[CountDownModal, onFailure]
	);

	const createPayload = useCallback(async () => {
		return {
			username: adminData.username,
			email: adminData.email,
			tenant_code: tenantCode,
			groups: ["TENANT"]
		};
	}, [adminData, tenantCode]);

	const handleChange = (e: { target: { id: any; value: any } }) => {
		handleAdminDataChange({
			id: e?.target?.id,
			value: e?.target?.value
		});
	};

	const proceedToCreateAdmin = useCallback(async () => {
		const isFieldsValid = isCreateAdminButtonEnabled && validateFields(true);

		if (isCreateAdminButtonEnabled && isFieldsValid) {
			const payload = await createPayload();
			try {
				const { data, errors, message } = await dispatch(createUser(payload)).unwrap();

				createUserSuccess(!errors?.length, message, data?.user_id);
			} catch (error) {
				// Added any as it is axios object
				const err = error as any;
				if (err?.response?.status === 401) {
					navigate(Routes.LOGIN, { state: { path: `${Routes?.ACCOUNT_MANAGEMENT}/${Routes?.CREATE_ACCOUNT}` } });
				}

				const errorObj = err?.response?.data?.errors;
				createUserFailure(errorObj);
			}
		}
	}, [isCreateAdminButtonEnabled, adminData, createUser, onSuccess, dispatch, validateFields]);

	useEffect(() => {
		getStoresInfo();
	}, []);

	const onSearchHandler = (value: string) => {
		if (value) {
			setSearchInput(value);
			setSearchSelected(true);
		} else {
			setSearchInput(null);
			setSearchSelected(false);
		}
	};

	return {
		onChange: onChangeHandler,
		searchInput,
		loading: loading === 1 || storeListLoading === 1,
		selectedValue,
		searchSelected,
		storesDataList: uniqBy(storesDataList, "tenant_code"),
		isActiveGroupList,
		getStoresInfo,
		onSearch: onSearchHandler,
		handleChange,
		goBack,
		onSuccess,
		adminData,
		fieldErrors,
		isCreateAdminButtonEnabled,
		validateFields,
		handleAdminDataChange,
		proceedToCreateAdmin
	};
}
